import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { combineLatest, EMPTY, filter, map, switchMap, take } from 'rxjs';
import { permissionLabel } from '../../../../models/permission.model';
import { VarResponse, VarService } from '../../../../services/var.service';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';

@Component({
  selector: 'app-variable-panel',
  imports: [MatIcon, MatIconButton, MatTooltip],
  templateUrl: './variable-panel.component.html',
  styleUrl: './variable-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablePanelComponent {
  private readonly _varService = inject(VarService);
  private readonly _editorState = inject(InteractionEditorStateService);
  private readonly _clipboard = inject(Clipboard);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _permissionService = inject(PermissionService);

  /**
   * Every variable endpoint needs `var:read`, which the editor's own
   * `interaction:read` does not imply. Without it nothing is fetched and the
   * section says which permission would fill it; the built-in ones need none.
   */
  protected readonly canReadVars = computed(() =>
    this._permissionService.hasPermission('var:read')
  );
  protected readonly varReadLabel = permissionLabel('var:read');

  protected readonly builtinVars = [
    {
      key: 'roepnaam',
      label: 'Roepnaam cliënt',
      tooltip: 'Roepnaam — in te stellen bij Client instellingen',
    },
  ];

  protected readonly variables = toSignal(
    combineLatest([
      toObservable(this._editorState.interaction),
      toObservable(this.canReadVars),
    ]).pipe(
      switchMap(([interaction, canReadVars]) => {
        if (interaction === null || !canReadVars) return EMPTY;
        return interaction.clientId
          ? this._varService
              .getClientVars(interaction.clientId)
              .pipe(map((vars) => vars.map((v) => ({ key: v.key, value: v.value }))))
          : this._varService
              .getAll()
              .pipe(map((vars) => vars.map((v) => ({ key: v.key, value: v.value }))));
      })
    ),
    { initialValue: [] }
  );

  protected readonly pendingDeletionVars = toSignal(
    toObservable(this.canReadVars).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this._varService.getPendingDeletionVars()),
      map((vars: VarResponse[]) =>
        vars
          .filter((v) => v.deleteAt)
          .map((v) => ({
            key: v.key,
            value: v.value,
            deleteAt: v.deleteAt!,
          }))
      )
    ),
    { initialValue: [] }
  );

  formatDeleteAt(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  copyVariable(key: string) {
    this._clipboard.copy(`[${key}]`);
    this._snackBar.open(`[${key}] gekopieerd`, '', { duration: 1500 });
  }
}
